<template>
  <div>
    <div class="layout-with-centered-max-width-child">
      <div>
        <div class="pdng-1">
          <div class="content-1">
            <header>
              <div>
                <div class="left-header">
                  <div>
                    <p class="name font-secondary">{{ results.studyName }}</p>
                  </div>
                  <div class="section-1">
                    <div>
                      <div class="study-label">
                        <span>{{ studyLabel }}</span>
                      </div>
                    </div>
                    <div>
                      <div class="study-status">
                        <span>Status: {{ studyOpenClosedStr }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="section-2">
                    <div>
                      <div class="study-surveys-started">
                        <span>Surveys started: </span>
                        <span class="count">{{ results.started }}</span>
                      </div>
                    </div>
                    <div>
                      <div class="study-surveys-completed">
                        <span>Surveys completed: </span>
                        <span class="count"
                          >{{ results.completed }} (out of {{ maxParticipants }})</span
                        >
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      v-if="showLinkToSurvey && fullAdminRights"
                      class="pdng-1 vertical link-to-survey font-size"
                      v-on:click="copyLink"
                    >
                      <Fai class="copy-link font-size" :icon="['far', 'clone']" />
                      <span id="linkToCopy">{{ surveyLink }}</span>
                      <span v-if="showCopiedBadge" class="copy-badge font-secondary font-size"
                        >copied</span
                      >
                    </div>
                  </div>
                  <div>
                    <div class="link-to-survey font-size">
                      <router-link :to="rawDataLink">View raw data</router-link>
                    </div>
                  </div>
                  <div>
                    <div class="link-to-survey font-size">
                      <router-link :to="publicStudyInfo">View Study Info</router-link>
                    </div>
                  </div>
                </div>
                <div class="right-header">
                  <div class="pdng-1 vertical">
                    <div class="exports">
                      <a :href="excelUrl" target="_blank" class="xcl-export">
                        <Fai :icon="['fal', 'file-excel']" size="2x" />
                      </a>
                      <span v-if="showPowerPoint">
                        <a :href="powerPointUrl" target="_blank" class="xcl-export">
                          <Fai :icon="['fal', 'file-powerpoint']" size="2x" />
                        </a>
                      </span>
                      <span v-if="showIdeaBook">
                        <a :href="ideaBookUrl" target="_blank" class="ppt-export">
                          <Fai :icon="['fal', 'lightbulb']" size="2x" />
                        </a>
                      </span>
                    </div>
                  </div>
                  <div>
                    <div
                      v-if="showManageParticipantsSelf && fullAdminRights"
                      class="link-to-survey font-size"
                    >
                      <router-link :to="manageParticipantLinkSelf">Manage Participants</router-link>
                    </div>
                    <div
                      v-if="showManageParticipantsLucid && fullAdminRights"
                      class="link-to-survey font-size"
                    >
                      <router-link :to="manageParticipantLinkLucid"
                        >Manage Participants</router-link
                      >
                    </div>
                  </div>
                  <div v-if="fullAdminRights && allowStudyToCloseAndReopen">
                    <div v-if="this.studyData.active" class="link-to-survey">
                      <button
                        class="pure-button bigmi-red-2-button"
                        v-on:click="openConfirmCloseStudyModal"
                      >
                        Close Study
                      </button>
                      <ConfirmCloseStudyModal
                        :showing="showConfirmCloseStudyModal"
                        :loading="closeButtonLoading"
                        @hide="closeConfirmCloseStudyModal"
                        @confirm="closeStudy"
                      />
                    </div>
                    <div v-else class="link-to-survey">
                      <button
                        v-if="closeButtonPristine"
                        class="pure-button bigmi-red-2-button"
                        v-on:click="openStudy"
                      >
                        Re-open Study
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </header>
            <section>
              <div class="tabs">
                <div v-for="(tab, i) in tabs" :class="tab.classObj" @click="onTabClick(i)" :key="i">
                  <div class="pdng-1 vertical">
                    <span>{{ tab.name }}</span>
                  </div>
                </div>
              </div>
              <div class="tab-content-container">
                <div v-if="isOverallTabSelected">
                  <ResultsOverall></ResultsOverall>
                </div>
                <div v-else>
                  <ResultsSecondary :index="selectedTabIndex"></ResultsSecondary>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ResultsOverall from "./ResultsOverall";
import ResultsSecondary from "./ResultsSecondary";
import ConfirmCloseStudyModal from "./ConfirmCloseStudyModal";
import {
  CSS_CLASS_NAME_SELECTED_TAB,
  GET_BACKEND_URL,
  GET_CURRENT_STUDY_EXCEL_URL, GET_CURRENT_STUDY_IDEABOOK_URL,
  GET_CURRENT_STUDY_POWERPOINT_URL,
  GET_FRONTEND_URL,
  GET_HTTP_CONFIG,
  GET_STUDY,
  LOAD_STUDIES_OF_USER,
  LOADING_PRISTINE,
  LOADING_STARTED,
  R_NAME_CREATE_STUDY_DONE,
  R_NAME_MANAGE_PARTICIPANTS,
  R_NAME_MANAGE_STUDY_SURVEY_QUALIFICATIONS_LANGUAGE,
  R_NAME_MANAGE_STUDY_SURVEY_QUALIFICATIONS_QUESTION,
  R_NAME_MY_PROFILE,
  R_NAME_PUBLIC_STUDY_INFO,
  R_NAME_RAW_DATA,
  RESET_SELECTED_QUALIFICATIONS,
  RESET_SURVEY_QUALIFICATIONS,
  STUDY_SOURCE_SELECTION_ACADEMIC,
  STUDY_SOURCE_SELECTION_DEMO,
  STUDY_SOURCE_SELECTION_LUCID,
} from "../../services/constants";
import http from "../../services/axios";

export default {
  name: "ResultsDesktop",
  components: {
    ResultsOverall,
    ResultsSecondary,
    ConfirmCloseStudyModal,
  },
  data() {
    return {
      selectedTabIndex: -1,
      showCopiedBadge: false,
      closeButtonStatus: LOADING_PRISTINE,
      showConfirmCloseStudyModal: false,
    };
  },
  computed: {
    ...mapGetters([GET_CURRENT_STUDY_EXCEL_URL, GET_CURRENT_STUDY_POWERPOINT_URL, GET_CURRENT_STUDY_IDEABOOK_URL]),
    studyResultsState() {
      return this.$store.state.studyResultsModule.studyResultsState;
    },
    results() {
      return this.studyResultsState.data;
    },
    studyState() {
      return this.$store.state.dataModule.getStudyState;
    },
    studyData() {
      return this.studyState.data;
    },
    maxParticipants() {
      return this.studyData.maxParticipants;
    },
    studyLabel() {
      return this.studyData.label;
    },
    studyOpenClosedStr() {
      return this.studyData.active ? "Open" : "Closed";
    },
    isSourcingLucid() {
      return this.studyData.sourcingMethod === STUDY_SOURCE_SELECTION_LUCID;
    },
    myProfileLink() {
      return {
        name: R_NAME_MY_PROFILE,
      };
    },
    rawDataLink() {
      return {
        name: R_NAME_RAW_DATA,
        params: {
          id: this.$route.params.id,
        },
      };
    },
    showLinkToSurvey() {
      return (
        this.studyData &&
        this.studyData.active === true &&
        (this.studyData.sourcingMethod === "self" || this.studyData.sourcingMethod === "custom")
      );
    },
    showManageParticipantsSelf() {
      return (
        this.studyData && this.studyData.sourcingMethod === "self" && this.studyData.active === true
      );
    },
    showManageParticipantsLucid() {
      return (
        this.studyData &&
        this.studyData.sourcingMethod === "lucid" &&
        this.studyData.active === true
      );
    },
    showPowerPoint() {
      return this.studyData && this.studyData.sourcingMethod !== "demo";
    },
    surveyLink() {
      let baseLink = this.$store.getters[GET_FRONTEND_URL](`survey/live/${this.$route.params.id}`);
      let queryParamsForLucidSample = "?source=LUCID&RID=[%RID%]&RISN=[%RSFN%]";
      return this.studyData.sourcingMethod === "self"
        ? baseLink
        : baseLink + queryParamsForLucidSample;
    },
    manageParticipantLinkSelf() {
      return {
        name: R_NAME_MANAGE_PARTICIPANTS,
        params: {
          study_id: this.$route.params.id,
        },
      };
    },
    manageParticipantLinkLucid() {
      return {
        name: R_NAME_CREATE_STUDY_DONE,
        query: {
          update: true,
          manage: true,
        },
      };
    },
    publicStudyInfo() {
      return {
        name: R_NAME_PUBLIC_STUDY_INFO,
        params: {
          id: this.$route.params.id,
        },
      };
    },
    tabNames() {
      return ["Overall", "Age", "Gender", "Prelim", "2 Market Segments", "3 Market Segments"];
    },
    tabs() {
      return this.tabNames.map((tabName, i) => {
        const isSelected = i === this.selectedTabIndex;
        return {
          name: tabName,
          isSelected,
          classObj: {
            [CSS_CLASS_NAME_SELECTED_TAB]: isSelected,
          },
        };
      });
    },
    isOverallTabSelected() {
      return this.selectedTabIndex === 0;
    },
    isAgeTabSelected() {
      return this.selectedTabIndex === 1;
    },
    isGenderTabSelected() {
      return this.selectedTabIndex === 2;
    },
    isPrelimTabSelected() {
      return this.selectedTabIndex === 3;
    },
    is2MarketSegmentsTabSelected() {
      return this.selectedTabIndex === 4;
    },
    is3MarketSegmentsTabSelected() {
      return this.selectedTabIndex === 5;
    },
    resultQuestions() {
      if (this.results) {
        return this.results.questions.map((question, i, questions) => {
          question.isLast = i === questions.length - 1;
          return question;
        });
      }
      return [];
    },
    excelUrl() {
      return this[GET_CURRENT_STUDY_EXCEL_URL];
    },
    powerPointUrl() {
      return this[GET_CURRENT_STUDY_POWERPOINT_URL];
    },
    ideaBookUrl() {
      return this[GET_CURRENT_STUDY_IDEABOOK_URL];
    },
    showIdeaBook() {
      return this.studyData.hasIdeas
    },
    closeButtonPristine() {
      return this.closeButtonStatus === LOADING_PRISTINE;
    },
    closeButtonLoading() {
      return this.closeButtonStatus === LOADING_STARTED;
    },
    fullAdminRights() {
      return this.$route.name === "results";
    },
    donePageRouteObj() {
      return {
        name: R_NAME_CREATE_STUDY_DONE,
        query: {
          update: true,
        },
      };
    },
    openStudyRouteObj() {
      return this.donePageRouteObj;
    },
    isStudyDemo() {
      return (
        this.$store.state.dataModule.getStudyState.data.sourcingMethod ===
        STUDY_SOURCE_SELECTION_DEMO
      );
    },
    isStudyAcademic() {
      return (
        this.$store.state.dataModule.getStudyState.data.sourcingMethod ===
        STUDY_SOURCE_SELECTION_ACADEMIC
      );
    },
    allowStudyToCloseAndReopen() {
      return !this.isStudyDemo && !this.isStudyAcademic;
    },
  },
  methods: {
    onTabClick(index) {
      this.selectedTabIndex = index;
    },
    copyLink() {
      let range = document.createRange();
      range.selectNode(document.getElementById("linkToCopy"));
      window.getSelection().removeAllRanges(); // clear current selection
      window.getSelection().addRange(range); // to select text
      document.execCommand("copy");
      window.getSelection().removeAllRanges(); // to deselect
      this.temporarilyShowCopiedBadge();
    },
    temporarilyShowCopiedBadge() {
      this.showCopiedBadge = true;
      setTimeout(() => {
        this.showCopiedBadge = false;
      }, 2000);
    },
    closeStudy() {
      this.closeButtonStatus = LOADING_STARTED;
      let studyID = this.$route.params.id;
      http
        .post(
          this.$store.getters[GET_BACKEND_URL](`studies/${studyID}/close`),
          {},
          this.$store.getters[GET_HTTP_CONFIG]
        )
        .then(() => {
          return this.$store.dispatch(GET_STUDY, studyID);
        })
        .then(() => {
          this.closeButtonStatus = LOADING_PRISTINE;
          this.closeConfirmCloseStudyModal();
          return this.$store.dispatch(RESET_SELECTED_QUALIFICATIONS);
        })
        .then(() => {
          return this.$store.dispatch(LOAD_STUDIES_OF_USER);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openStudy() {
      this.$router.push(this.openStudyRouteObj);
    },
    openConfirmCloseStudyModal() {
      this.showConfirmCloseStudyModal = true;
    },
    closeConfirmCloseStudyModal() {
      this.showConfirmCloseStudyModal = false;
    },
  },
  created() {
    this.selectedTabIndex = 0;
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../scss/main";

.loading {
  color: $red-2;
}

header {
  > div {
    display: flex;

    > div {
      width: 50%;
    }
  }

  .left-header {
    p.name {
      color: $red-2;
      font-weight: bold;
    }

    .study-label,
    .study-status {
      color: $cyan-1;
      font-size: 15px;
      line-height: 22px;
    }

    .section-1 {
      padding: 0 0 1em 0;
    }

    .section-2 {
      padding-bottom: 1em;
      color: $cyan-1;
      font-size: 15px;
      line-height: 25px;

      .count {
        color: $red-2;
        font-size: 15px;
      }
    }
  }

  .right-header {
    > div {
      display: flex;
      justify-content: flex-end;
    }
  }

  a {
    color: $cyan-1;
  }

  .exports {
    .xcl-export {
      padding: 1.5em 0.5em 0.5em;
      margin: 0 0.5em 0 0;
    }

    .ppt-export {
      padding: 1.5em 0 0.5em 0.5em;
    }
  }
}

.content-1 {
  margin: 0 60px;
}

.tabs {
  display: flex;

  > div {
    width: 16.66%;
    display: flex;
    justify-content: center;
    margin: 0 1px;
    cursor: pointer;
    background-color: $cyan-blue-2;
    color: $cyan-1;
    font-size: 15px;

    &.selected-tab {
      background-color: $cyan-blue-3;
      color: $white-1;
    }
  }
}

.link-to-survey {
  padding-bottom: 1em;
  display: flex;
  cursor: pointer;

  .copy-link {
    display: inline-block;
    padding-right: 0.5em;
  }

  .copy-badge {
    padding-left: 0.5em;
    color: $red-2;
  }
}

.font-size {
  font-size: 15px;
}

.button {
  border-color: $red-2;
  font-weight: bold;
  background-color: $white-1;
  border-width: medium;
  color: $red-2;
  border-radius: 5px;
  padding: 10px 20px;
}
</style>
